import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { noop, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import {
  BASE_PERMISSIONS,
  baseAssignmentsSelectors,
  BaseAvatarSize,
  BaseCoreState,
  baseCustomerDashboardActions,
  baseCustomerDashboardSelectors,
  baseInquirySelectors,
  BaseInquiryStatus,
  BaseModalService,
  BaseRolesService,
  CustomerAssignmentListItemResponse,
  CustomerDashboardResponse,
  CustomerOfferListItemResponse,
} from 'base';
import { BaseCustomerInquiryListItemType } from '../inquiry/_interfaces/base-inquiry.type';

@Component({
  selector: 'base-customer-dashboard',
  templateUrl: './base-customer-dashboard.component.html',
})
export class BaseCustomerDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('staffRequest', { static: true }) staffRequest: ElementRef;
  dashboardData: CustomerDashboardResponse | undefined;
  customerOffers: CustomerOfferListItemResponse[];
  unpublishedInquiries: BaseCustomerInquiryListItemType[];
  upcomingEPAssignments: CustomerAssignmentListItemResponse[] = [];
  upcomingMLPAssignments: CustomerAssignmentListItemResponse[] = [];
  upcomingEPAssignmentsTotalCount: number;
  upcomingMLPAssignmentsTotalCount: number;
  modalRef: NgbModalRef;
  canAdminAssignments: boolean;
  canAdminInquiry: boolean;
  canPublishInquiry: boolean;
  avatarSizes = BaseAvatarSize;
  private destroy$ = new Subject();

  constructor(
    private store: Store<BaseCoreState>,
    private rolesService: BaseRolesService,
    private modalService: BaseModalService
  ) {}

  ngOnInit() {
    this.canPublishInquiry = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PUBLISH_INQUIRY);

    this.canAdminInquiry = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_ADMIN_INQUIRY);

    this.canAdminAssignments = this.rolesService.hasCustomerFeaturePermission(
      BASE_PERMISSIONS.FEATURE_ADMIN_ASSIGNMENTS
    );

    this.loadDashboardData();
    this.loadCustomerOffers();
    this.loadCustomerInquiries();
    this.loadUpcomingEPAssignments();
    this.loadUpcomingMLPAssignments();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  loadDashboardData() {
    this.store
      .select(baseCustomerDashboardSelectors.getCustomerDashboardData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: CustomerDashboardResponse) => {
          this.dashboardData = data;
        },
      });
  }

  loadCustomerOffers() {
    this.store
      .select(baseCustomerDashboardSelectors.getCustomerOfferData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (offers: CustomerOfferListItemResponse[]) => {
          // ensures that there will only be past offers
          const filtered: CustomerOfferListItemResponse[] = offers?.filter(
            (offer) => offer.createdAt && new Date().getTime() - new Date(offer.createdAt).getTime() >= 0
          );

          // offers will be sorted by relevance
          this.customerOffers = filtered?.sort((a, b) =>
            a.createdAt && b.createdAt && new Date(a.createdAt).getTime() < new Date(b.createdAt).getTime() ? 1 : -1
          );
        },
      });
  }

  loadCustomerInquiries() {
    this.store
      .select(baseInquirySelectors.getInquiries)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state),
        map((response) => response.data)
      )
      .subscribe({
        next: (state) => {
          this.unpublishedInquiries = state?.data?.filter(
            (inquiry: any) => inquiry.status === BaseInquiryStatus.unpublished
          );
        },
      });
  }

  loadUpcomingEPAssignments() {
    this.store
      .select(baseAssignmentsSelectors.getUpcomingEPAssignments)
      .pipe(
        takeUntil(this.destroy$),
        filter((assignmentState) => assignmentState.ok === true),
        map(
          (assignmentState) =>
            assignmentState.data as { assignments: CustomerAssignmentListItemResponse[]; totalCount: number }
        )
      )
      .subscribe((data) => {
        this.upcomingEPAssignments = data.assignments;
        this.upcomingEPAssignmentsTotalCount = data.totalCount;
      });
  }

  loadUpcomingMLPAssignments() {
    this.store
      .select(baseAssignmentsSelectors.getUpcomingMLPAssignments)
      .pipe(
        takeUntil(this.destroy$),
        filter((assignmentState) => assignmentState.ok === true),
        map(
          (assignmentState) =>
            assignmentState.data as { assignments: CustomerAssignmentListItemResponse[]; totalCount: number }
        )
      )
      .subscribe((data) => {
        this.upcomingMLPAssignments = data.assignments;
        this.upcomingMLPAssignmentsTotalCount = data.totalCount;
      });
  }

  onOfferRefresh() {
    this.store.dispatch(baseCustomerDashboardActions.loadCustomerOfferData());
  }

  openNewStaffRequestModal() {
    this.modalRef = this.modalService.open(
      {
        headText: 'inquiry.new',
        bodyTemplate: this.staffRequest,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );
    this.modalRef.result.then(noop, noop);
  }
}
